import ShopInformation from './models/ShopInformation'
import {
  fetchShopBaseInformation as fetchShopBaseInformationFromServer
} from '@/utils/shop-information-manager/api'
import ChannelManager from '@/utils/channel-manager'

export type ShopInformationCallback = (shopInformation?: ShopInformation) => void

/**
 * 管理店铺信息数据源的获取和回调处理
 * @class
 */
class ShopInformationDataSourceManager {
  /** 单例实例 */
  private static _manager: ShopInformationDataSourceManager
  /** 渠道管理器实例 */
  private channelManager: ChannelManager

  /**
   * 获取单例实例
   * @static
   * @returns {ShopInformationDataSourceManager} 单例实例
   */
  static instance(): ShopInformationDataSourceManager {
    if (this._manager === undefined) {
      this._manager = new ShopInformationDataSourceManager()
    }
    return this._manager
  }

  /** 店铺信息缓存 { [店铺ID]: 信息对象 } */
  private readonly _shopInformationList: { [shopId: string]: { information?: ShopInformation } }
  /** 店铺回调函数映射表 { [店铺ID]: 回调函数数组 } */
  private readonly _shopCallbacks: { [shopId: string]: ShopInformationCallback[] }

  /**
   * 私有构造函数（单例模式）
   * @private
   */
  private constructor() {
    this._shopInformationList = {}
    this._shopCallbacks = {}
    this.channelManager = ChannelManager.instance()
  }

  /**
   * 添加店铺回调函数到指定店铺的监听列表
   * @private
   * @param {string} shopId - 店铺ID
   * @param {ShopInformationCallback} callback - 回调函数
   */
  private _addShopCallback(shopId: string, callback: ShopInformationCallback) {
    let shopInformationCallbacks = this._shopCallbacks[shopId]

    if (shopInformationCallbacks === undefined) {
      shopInformationCallbacks = []
      this._shopCallbacks[shopId] = shopInformationCallbacks
    }

    shopInformationCallbacks.push(callback)
  }

  /**
   * 执行指定店铺的所有回调函数并清空回调列表
   * @private
   * @param {string} shopId - 店铺ID
   * @param {ShopInformation} [shopInformation] - 可选的店铺信息
   */
  private _runShopCallback(shopId: string, shopInformation?: ShopInformation) {
    const shopInformationCallbacks = this._shopCallbacks[shopId]

    if (shopInformationCallbacks !== undefined) {
      shopInformationCallbacks.forEach(callback => callback(shopInformation))
      delete this._shopCallbacks[shopId]
    }
  }

  /**
   * 将店铺渠道信息存储到ChannelManager中
   * @private
   * @param {string} shopId - 店铺ID
   * @param {ShopInformation} shopInformation - 店铺信息
   */
  private _storeShopChannels(
    shopId: string,
    shopInformation: ShopInformation
  ): void {
    if (shopInformation.channels && shopInformation.channels.length > 0) {
      this.channelManager.setShopChannels(shopId, shopInformation.channels)
    }
  }

  /**
   * 获取店铺信息（支持缓存和回调机制）
   * 同时将店铺的渠道信息存储到ChannelManager中，用于判断当前用户使用的店铺有哪些渠道
   * @async
   * @param {string} shopId - 需要获取信息的店铺ID
   * @param {ShopInformationCallback} callback - 获取到信息后的回调函数
   * @returns {Promise<void>} 异步操作Promise
   * @throws {Error} 当请求失败时抛出异常
   */
  async fetchShopInformation(
    shopId: string,
    callback: ShopInformationCallback
  ): Promise<void> {
    this._addShopCallback(shopId, callback)
    const shopInformation = this._shopInformationList[shopId]

    if (shopInformation === undefined) {
      this._shopInformationList[shopId] = {}

      try {
        const { data } = await fetchShopBaseInformationFromServer(shopId)
        this._shopInformationList[shopId].information = data
        // 将当前店铺的渠道信息存储到ChannelManager
        this._storeShopChannels(shopId, data)
        this._runShopCallback(shopId, data)
        return Promise.resolve()
      } catch (e) {
        delete this._shopInformationList[shopId]
        this._runShopCallback(shopId)
        throw e // 将异常传递给调用者
      }
    } else if (shopInformation.information !== undefined) {
      // 确保渠道信息也被存储
      this._storeShopChannels(shopId, shopInformation.information)
      this._runShopCallback(shopId, shopInformation.information)
      return Promise.resolve()
    } else {
      return Promise.resolve()
    }
  }
}

export { ShopInformationDataSourceManager }
